import {View} from 'react-native'
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'

import {type DocumentMaterialRef, totalsOf} from '#/lib/document/material'
import {
  type DocumentAudience,
  type DocumentEmbedView,
} from '#/lib/document/types'
import {
  documentTypeLabel,
  formatFileSize,
  isDocumentPreviewable,
} from '#/lib/document/util'
import {useSession} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {DocumentMaterialBadges} from '#/components/documents/DocumentMaterialBadges'
import {Text} from '#/components/Typography'
import {PdfCarousel} from './document/PdfCarousel.web'

export type {DocumentEmbedView}

/**
 * Renders a `app.bsky.embed.document#view`.
 *
 * PDFs get the full carousel/reader treatment. Everything else (Word, Excel,
 * Markdown, …) falls back to a static card, since we can't render those in the
 * browser without shipping another heavyweight parser.
 */
export function DocumentEmbed({
  embed,
  authorDid,
  audience,
  materialRef,
  postUri,
}: {
  embed: DocumentEmbedView
  authorDid: string
  audience?: DocumentAudience
  /** The post's declared page material, for the badges and the play. */
  materialRef?: DocumentMaterialRef
  postUri?: string
}) {
  const {currentAccount} = useSession()

  if (isDocumentPreviewable(embed.mimeType)) {
    return (
      <PdfCarousel
        embed={embed}
        authorDid={authorDid}
        altOverride={embed.alt}
        audience={audience}
        materialRef={materialRef}
        postUri={postUri}
        /*
         * Asking the session rather than taking one more prop: "is this mine"
         * is a fact about the reader, not about the post, and every surface that
         * draws a document already knows both.
         */
        isAuthor={!!currentAccount && currentAccount.did === authorDid}
      />
    )
  }

  return <DocumentCard embed={embed} materialRef={materialRef} />
}

function DocumentCard({
  embed,
  materialRef,
}: {
  embed: DocumentEmbedView
  materialRef?: DocumentMaterialRef
}) {
  const {_} = useLingui()
  const t = useTheme()

  return (
    <View
      style={[
        a.flex_row,
        a.align_center,
        a.gap_md,
        a.mt_sm,
        a.p_md,
        a.rounded_md,
        a.border,
        {
          borderColor: t.palette.contrast_200,
          backgroundColor: t.palette.contrast_25,
        },
      ]}>
      <View
        style={[
          a.rounded_sm,
          a.p_sm,
          a.justify_center,
          a.align_center,
          {width: 48, height: 48, backgroundColor: t.palette.primary_100},
        ]}>
        <FontAwesomeIcon icon="file" size={22} color={t.palette.primary_500} />
      </View>

      <View style={[{flex: 1}, a.gap_2xs]}>
        <Text style={[a.text_sm, a.font_bold, t.atoms.text]} numberOfLines={1}>
          {embed.title ?? _(msg`Document`)}
        </Text>
        <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
          {[
            documentTypeLabel(embed.mimeType),
            embed.pageCount ? _(msg`${embed.pageCount} pages`) : null,
            embed.fileSize ? formatFileSize(embed.fileSize) : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Text>
        <DocumentMaterialBadges totals={totalsOf(materialRef)} />
      </View>
    </View>
  )
}
